<template>
  <div class="tw-p-5">
    <header class="tw-flex tw-flex-col tw-gap-2 tw-mb-5">
      <h1 class="tw-text-2xl tw-font-bold tw-text-center">
        Meeting Preferences
      </h1>
    </header>

    <div class="tw-flex tw-flex-col tw-gap-8">
      <section>
        <h2>Data Retention</h2>

        <div class="tw-flex tw-gap-10">
          <div>
            <h3 class="tw-font-bold tw-mb-1">
              Delete recordings after
            </h3>
            <StakeRadioSelect
              v-model="recordingRetention"
              name="Recordings"
              :options="retentionOptions"
            />
          </div>

          <div>
            <h3 class="tw-font-bold tw-mb-1">
              Delete transcripts after
            </h3>
            <StakeRadioSelect
              v-model="transcriptRetention"
              name="Transcripts"
              :options="retentionOptions"
            />
          </div>
        </div>
      </section>

      <section>
        <h2>Bot Scheduling</h2>

        <div class="">
          <h3 class="tw-font-bold tw-mb-1">
            What meetings should the bot join?
          </h3>
          <StakeRadioSelect
            v-model="joinPreference"
            name="Join"
            :options="joinOptions"
          />
        </div>
      </section>

      <section class="">
        <h2>Bot Appearance</h2>

        <div class="">
          <h3 class="tw-font-bold tw-mb-1">
            Name
          </h3>
          <input
            v-model="botName"
            type="text"
            class="tw-w-full tw-py-2 tw-px-3 tw-border tw-border-solid tw-border-gray-300 tw-rounded tw-text-base"
            placeholder="Meeting Notetaker"
            maxlength="100"
          >
        </div>
      </section>

      <!-- Meeting Type Settings -->
      <div class="field">
        <label class="label">Default Meeting Type</label>
        <div class="control">
          <o-select
            v-model="defaultMeetingType"
            expanded
          >
            <option
              v-for="type in meetingTypeOptions"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </o-select>
        </div>
        <p class="help">
          This meeting type will be applied to all bot-recorded meetings
        </p>
      </div>

      <section>
        <h2>Notifications</h2>

        <div>
          <label class="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer">
            <input
              v-model="sendMeetingNotesReadyEmail"
              type="checkbox"
              class="tw-mt-1"
            >
            <span>
              <span class="tw-block tw-font-bold">Email me when meeting notes are ready</span>
              <span class="tw-block tw-text-sm tw-text-gray-600">
                Turn this off to skip the email sent after a meeting recording finishes processing.
              </span>
            </span>
          </label>
        </div>
      </section>

      <!-- Custom Prompts Section -->
      <section>
        <h2>AI Prompts Customization</h2>
        
        <div class="">
          <h3 class="tw-font-bold tw-mb-1">
            Meeting Type
          </h3>
          <StakeRadioSelect
            v-model="selectedPromptMeetingType"
            name="PromptMeetingType"
            :options="meetingTypeOptions"
            @change="loadPromptsForMeetingType"
          />
        </div>

        <div 
          v-if="selectedPromptMeetingType && prompts[selectedPromptMeetingType]"
          class="tw-mt-4"
        >
          <div class="tw-space-y-6">
            <div 
              v-for="(promptData, promptType) in prompts[selectedPromptMeetingType]"
              :key="promptType"
              class="tw-border tw-border-gray-300 tw-rounded tw-p-4"
            >
              <div class="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <h4 class="tw-font-bold tw-capitalize">
                  {{ promptType.replace('_', ' ') }} Prompt
                </h4>
                <div class="tw-flex tw-gap-2">
                  <button
                    v-if="promptData.is_custom"
                    class="tw-text-sm tw-px-3 tw-py-1 tw-bg-yellow-100 tw-border-transparent tw-text-yellow-800 tw-rounded"
                    @click="revertPrompt(promptType)"
                  >
                    Revert to Default
                  </button>
                  <button
                    class="tw-text-sm tw-px-3 tw-py-1 tw-bg-blue-100 tw-border-transparent tw-text-blue-800 tw-rounded"
                    @click="toggleEditPrompt(promptType)"
                  >
                    {{ editingPrompts[promptType] ? 'Cancel' : 'Edit' }}
                  </button>
                </div>
              </div>
              
              <div v-if="editingPrompts[promptType]">
                <textarea
                  v-model="editedPromptTexts[promptType]"
                  class="tw-w-full tw-h-40 tw-p-3 tw-border tw-border-gray-300 tw-rounded tw-text-sm tw-font-mono"
                  placeholder="Enter your custom prompt..."
                />
                <div class="tw-flex tw-gap-2 tw-mt-2">
                  <button
                    class="tw-px-4 tw-py-2 tw-bg-green-600 tw-text-white tw-border-transparent tw-rounded tw-text-sm"
                    :disabled="savingPrompt"
                    @click="savePrompt(promptType)"
                  >
                    {{ savingPrompt ? 'Saving...' : 'Save' }}
                  </button>
                  <button
                    class="tw-px-4 tw-py-2 tw-bg-gray-400 tw-text-white tw-border-transparent tw-rounded tw-text-sm"
                    @click="cancelEditPrompt(promptType)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              
              <div v-else>
                <div class="tw-bg-gray-50 tw-p-3 tw-rounded tw-text-sm tw-max-h-40 tw-overflow-auto tw-whitespace-pre-wrap tw-break-words">
                  {{ promptData.text }}
                </div>
                <div class="tw-mt-2 tw-text-xs tw-text-gray-500">
                  {{ promptData.is_custom ? 'Custom prompt' : 'Default prompt' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Save Button -->
      
      <div class="field mt-4">
        <div class="tw-text-center tw-text-sm">
          <transition
            :name="statusTransition"
            mode="out-in"
          >
            <span
              v-if="loading"
              key="loading"
              class="tw-text-gray-500"
            >
              <i class="fa fa-spinner fa-spin" />
            </span>

            <span
              v-else-if="saving"
              key="saving"
              class="tw-text-gray-500"
            >
              <i class="fa fa-spinner fa-spin" />
              Saving
            </span>

            <span
              v-else-if="saved"
              key="saved"
              class="tw-text-emerald-500"
            >
              <i class="fa fa-check" />
              Changes saved
            </span>

            <span
              v-else
              key="placeholder"
              class="tw-opacity-0"
            >Placeholder
            </span>
          </transition>
        </div>
        <div class="control">
          <button
            class="button is-primary"
            :class="{'is-loading': saving}"
            @click="savePreferences"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import StakeRadioSelect from '@stakeui/StakeRadioSelect.vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { mapStores } from 'pinia';


export default {
  name: 'MeetingBotSettings',
  components: {
    StakeRadioSelect,
  },
  props: [/* Inputs */],
  data() {
    return {
      loading: false,
      saving: false,
      saved: false,

      recordingRetention: '',
      transcriptRetention: '',
      joinPreference: '',
      defaultMeetingType: '',
      botName: '',
      sendMeetingNotesReadyEmail: true,

      retentionOptions: [
        { label: '7 years', value: '2555' },
        { label: '3 years', value: '1095' },
        { label: '1 year', value: '365' },
        { label: '1 month', value: '30' },
        { label: 'Immediately after processing', value: '0' },
      ],
      joinOptions: [
        { label: 'Don\'t join any meetings', value: 'none' },
        { label: 'Only join meetings that I\'m the host of', value: 'host' },
        { label: 'Join all meetings on my calendar', value: 'all' },
      ],
      meetingTypeOptions: [],
      
      // Custom prompts
      selectedPromptMeetingType: '',
      prompts: {},
      editingPrompts: {},
      editedPromptTexts: {},
      savingPrompt: false,
    };
  },
  computed: { /*
    Creates a new property
    Updates when any dependant property changes */
    ...mapStores(useAdvisorStore),
    preferences() {
      if (this.recordingRetention === '' || this.transcriptRetention === '' || this.joinPreference === '' || this.defaultMeetingType === '') return null;

      return {
        delete_recordings_after: this.encodeRetentionValue(this.recordingRetention),
        delete_transcripts_after: this.encodeRetentionValue(this.transcriptRetention),
        meetings_to_join: this.joinPreference,
        default_meeting_type: this.defaultMeetingType,
        meeting_bot_name: this.botName,
        send_meeting_notes_ready_email: this.sendMeetingNotesReadyEmail,
      };
    },
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    statusTransition() {
      return !(this.loading || this.saving || this.saved) ? 'fade-slow' : 'fade';
    },
  },
  beforeCreate() {},
  created() {
    this.getPreferences();
    this.loadCustomPrompts();
  },
  mounted() {},
  updated() {},
  methods: {
    async getPreferences() {
      this.loading = true;
      const res = await fetch('/api/v1/advisors/preferences/', {
        method: 'GET',
        headers: this.headers,
      });
      const data = await res.json();
      console.log('PREFERENCES', data);
      this.loading = false;

      this.recordingRetention = this.parseRetentionValue(data.delete_recordings_after);
      this.transcriptRetention = this.parseRetentionValue(data.delete_transcripts_after);
      this.joinPreference = data?.meetings_to_join || '';
      this.defaultMeetingType = data?.default_meeting_type || '';
      this.botName = data?.meeting_bot_name || '';
      this.sendMeetingNotesReadyEmail = data?.send_meeting_notes_ready_email !== false;

      this.meetingTypeOptions = data.meeting_type_choices;
    },
    parseRetentionValue(value) {
      if (value === null) return 'keep';
      return value.toString();
    },
    encodeRetentionValue(value) {
      if (value === 'keep') return null;
      return parseInt(value);
    },
    async savePreferences() {
      this.saving = true;

      const res = await fetch('/api/v1/advisors/preferences/', {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(this.preferences),
      });

      const data = await res.json();
      console.log(data);
      this.saving = false;

      this.saved = true;
      setTimeout(() => this.saved = false, 2000);
    },
    
    async loadCustomPrompts() {
      try {
        const res = await fetch('/api/v1/custom-prompts/', {
          method: 'GET',
          headers: this.headers,
        });
        const data = await res.json();
        this.prompts = data;
        
        // Set initial meeting type if not set
        if (!this.selectedPromptMeetingType && this.meetingTypeOptions.length > 0) {
          this.selectedPromptMeetingType = this.meetingTypeOptions[0].value;
        }
      } catch (error) {
        console.error('Error loading custom prompts:', error);
      }
    },
    
    loadPromptsForMeetingType() {
      // Reset editing states when switching meeting types
      this.editingPrompts = {};
      this.editedPromptTexts = {};
    },
    
    toggleEditPrompt(promptType) {
      if (this.editingPrompts[promptType]) {
        this.cancelEditPrompt(promptType);
      } else {
        this.editingPrompts = { 
          ...this.editingPrompts, 
          [promptType]: true 
        };
        this.editedPromptTexts = { 
          ...this.editedPromptTexts, 
          [promptType]: this.prompts[this.selectedPromptMeetingType][promptType].text 
        };
      }
    },
    
    cancelEditPrompt(promptType) {
      this.editingPrompts = { 
        ...this.editingPrompts, 
        [promptType]: false 
      };
      delete this.editedPromptTexts[promptType];
    },
    
    async savePrompt(promptType) {
      if (!this.editedPromptTexts[promptType] || this.editedPromptTexts[promptType].trim() === '') {
        alert('Prompt text cannot be empty');
        return;
      }
      
      this.savingPrompt = true;
      
      try {
        const res = await fetch('/api/v1/custom-prompts/', {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({
            meeting_type: this.selectedPromptMeetingType,
            prompt_type: promptType,
            prompt_text: this.editedPromptTexts[promptType],
          }),
        });
        
        const data = await res.json();
        
        if (data.success) {
          // Update local state
          this.prompts[this.selectedPromptMeetingType][promptType] = {
            text: this.editedPromptTexts[promptType],
            is_custom: true,
          };
          
          // Exit edit mode
          this.editingPrompts = { 
            ...this.editingPrompts, 
            [promptType]: false 
          };
          delete this.editedPromptTexts[promptType];
          
          alert('Prompt saved successfully!');
        } else {
          alert('Failed to save prompt: ' + (data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error saving prompt:', error);
        alert('Failed to save prompt');
      } finally {
        this.savingPrompt = false;
      }
    },
    
    async revertPrompt(promptType) {
      if (!confirm('Are you sure you want to revert to the default prompt? This will delete your custom prompt.')) {
        return;
      }
      
      try {
        const res = await fetch('/api/v1/custom-prompts/delete/', {
          method: 'DELETE',
          headers: this.headers,
          body: JSON.stringify({
            meeting_type: this.selectedPromptMeetingType,
            prompt_type: promptType,
          }),
        });
        
        const data = await res.json();
        
        if (data.success) {
          // Reload prompts to get the default prompt back
          await this.loadCustomPrompts();
          alert('Reverted to default prompt successfully!');
        } else {
          alert('Failed to revert prompt: ' + (data.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error reverting prompt:', error);
        alert('Failed to revert prompt');
      }
    },
  },
};
</script>


<style scoped>
section > h2 {
  @apply tw-font-bold tw-text-lg;
  @apply tw-mb-1;
}
section > div {
  @apply tw-py-4;
  @apply tw-border-0 tw-border-t tw-border-solid tw-border-gray-300;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter, .fade-leave-to, .fade-slow-leave-to {
  opacity: 0;
}
.fade-slow-leave-active {
  transition: opacity 0.5s ease;
}
</style>
